import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { SafetyCertificateFilled } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Controller,
  UseFormRegisterReturn,
  useForm,
  useWatch,
} from 'react-hook-form';
import { createGlobalStyle } from 'styled-components';
import StyledButton from 'app/components/StyledButton';
import { MultiSelectPopover } from 'app/components/MultiSelectPopover';
import {
  selectLanguage,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { getDataAgentApi } from 'app/services/checkTicketApi';
import { getLOVPaymentMethodReservation } from '@/app/services/commonInfoServices';
import {
  ApproveReservationPaymentPayload,
  approveReservationPaymentApi,
  getReservationBookingApproveHistoriesApi,
} from 'app/services/reservationHistoryApi';
import {
  ReservationHistoryItem,
} from 'app/pages/reservation-history/store';
import {
  getCreatedDateSearchRange,
  getDateParam,
  getDefaultCreatedDateFilter,
  parseDateParam,
} from 'app/pages/reservation-history/dateRangeUtils';
import {
  getMockLOVDisplayValue,
  ReservationHistory_ApproveStatus_LOV,
} from 'app/pages/reservation-history/config';
import { DataTableServer } from '@/components/ui/data-table-server-pagination';
import {
  DatePickerWithRange,
  DateRange,
} from '@/components/ui/date-picker-with-range';
import {
  ApprovalConfirmModal,
  ApprovalConfirmModalRow,
} from '@/components/ui/approval-confirm-modal';
import {
  cn,
  formatDateBEtoUTCFormat,
  showReservationErrorToast,
} from '@/lib/utils';
import { format, isValid } from 'date-fns';

const ACTION_ICON_BUTTON_CLASS =
  'inline-flex h-9 w-9 items-center justify-center rounded border-0 bg-[linear-gradient(79.97deg,#F06F24_-7.79%,#F8B117_107.61%)] p-0 text-white shadow-none outline-none hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70';

const CONTROL_BUTTON_CLASS = 'reservation-approval-filter-control';
const DATE_RANGE_FILTER_CLASS =
  '!h-10 !rounded-lg !border-[#d9d9d9] !bg-white !px-3 !text-sm !font-normal !text-[#111111] !shadow-none hover:!border-[#f06f24]';

const ReservationApprovalFilterStyles = createGlobalStyle`
  .${CONTROL_BUTTON_CLASS} {
    height: 40px !important;
    min-height: 40px !important;
    border-color: #d9d9d9 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
  }
`;

type FilterState = {
  createdDateFrom?: string;
  createdDateTo?: string;
  reservationNo?: string;
  approveStatus?: string;
  paymentMethod?: string;
  agentId?: string;
};

const APPROVE_STATUS_OPTIONS = ReservationHistory_ApproveStatus_LOV.map(
  item => ({
    value: item.code,
  }),
);

const getResponsePayload = (response: any) =>
  response?.data?.data ?? response?.data;

const getResponseItems = (payload: any): ReservationHistoryItem[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.list)) return payload.list;

  return [];
};

const getResponseTotal = (payload: any, items: ReservationHistoryItem[]) =>
  Number(
    payload?.totalCount ??
      payload?.total ??
      payload?.totalElements ??
      payload?.totalRecords ??
      payload?.count ??
      items.length,
  );

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '--';

  const date = new Date(formatDateBEtoUTCFormat(dateStr.slice(0, 19)));
  return isValid(date) ? format(date, 'dd/MM/yyyy HH:mm:ss') : dateStr;
};


const formatMoney = (value?: string | number, currency?: string) => {
  if (value === undefined || value === null || value === '') return '--';
  const amount =
    typeof value === 'number'
      ? value.toLocaleString('vi-VN')
      : `${value}`.replaceAll(',', '.');

  return currency ? `${amount} ${currency}` : amount;
};

const getPaymentMethodLabel = (value: any, options: any[]) => {
  const normalizedValue = `${value ?? ''}`;
  if (!normalizedValue) return '--';

  const matchedPayment = options.find(
    item =>
      `${item?.mapVGS ?? ''}` === normalizedValue ||
      `${item?.code ?? ''}` === normalizedValue ||
      `${item?.id ?? ''}` === normalizedValue,
  );

  return matchedPayment?.name ?? matchedPayment?.code ?? normalizedValue;
};

const getApproveStatusLabel = (language: string, value?: string) =>
  value
    ? getMockLOVDisplayValue(
        language,
        value,
        ReservationHistory_ApproveStatus_LOV,
      )
    : '--';

const getApproveStatusBadgeClass = (value?: string) => {
  switch (value) {
    case 'PendingApprove':
      return 'bg-[#fff7dc] text-[#d9a51a]';
    case 'Approved':
      return 'bg-[#e8fff4] text-[#21c77a]';
    case 'PaymentRejected':
    case 'Cancelled':
    case 'Canceled':
      return 'bg-[#f1f1f1] text-[#9b9b9b]';
    default:
      return 'bg-[#f1f1f1] text-[#8c8c8c]';
  }
};

const getCountdown = (expireAt?: string, expiredLabel = 'Hết hạn') => {
  if (!expireAt) return '--';

  const expireDate = new Date(formatDateBEtoUTCFormat(expireAt));
  if (!isValid(expireDate)) return '--';

  const diff = expireDate.getTime() - Date.now();
  if (diff <= 0) return expiredLabel;

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map(value => `${value}`.padStart(2, '0'))
    .join(':');
};

const isExpired = (expireAt?: string) => {
  if (!expireAt) return false;

  const expireDate = new Date(formatDateBEtoUTCFormat(expireAt));
  return isValid(expireDate) && expireDate.getTime() <= Date.now();
};

const getExpireAt = (item?: ReservationHistoryItem | null) =>
  item?.payment?.paymentDueAt ?? item?.expireAt;

const shouldShowHoldTimeCountdown = (approveStatus?: string) =>
  approveStatus === 'PendingApprove';

export const ReservationOrderApprovalTab: React.FC = () => {
  const history = useHistory();
  const translations = useSelector(selectTranslations);
  const language = useSelector(selectLanguage);
  const t = useCallback(
    (key: string, fallback: string) => translations?.[key] ?? fallback,
    [translations],
  );
  const defaultDateRange = useMemo(() => {
    const defaultCreatedDateFilter = getDefaultCreatedDateFilter();

    return {
      from: parseDateParam(defaultCreatedDateFilter.createdAtFrom),
      to: parseDateParam(defaultCreatedDateFilter.createdAtTo),
    };
  }, []);
  const defaultFilters = useMemo<FilterState>(
    () => ({
      createdDateFrom: getDateParam(defaultDateRange.from),
      createdDateTo: getDateParam(defaultDateRange.to),
    }),
    [defaultDateRange],
  );
  const approveStatusOptions = useMemo(
    () =>
      APPROVE_STATUS_OPTIONS.map(item => ({
        label: getMockLOVDisplayValue(
          language,
          item.value,
          ReservationHistory_ApproveStatus_LOV,
        ),
        value: item.value,
      })),
    [language],
  );
  const { control, getValues, register, setValue } = useForm<FilterState>({
    defaultValues: defaultFilters,
  });
  const createdDateFrom = useWatch({ control, name: 'createdDateFrom' });
  const createdDateTo = useWatch({ control, name: 'createdDateTo' });
  const createdDateRange = useMemo<DateRange>(
    () => ({
      from: parseDateParam(createdDateFrom),
      to: parseDateParam(createdDateTo),
    }),
    [createdDateFrom, createdDateTo],
  );
  const [data, setData] = useState<ReservationHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<any[]>([]);
  const [agentOptions, setAgentOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [selectedItem, setSelectedItem] =
    useState<ReservationHistoryItem | null>(null);
  const [actionLoading, setActionLoading] =
    useState<ApproveReservationPaymentPayload['approveStatus']>();
  const [, setTick] = useState(0);

  const fetchData = useCallback(
    async (nextPagination = pagination, nextFilters = filters) => {
      const createdDateRange = getCreatedDateSearchRange(
        nextFilters.createdDateFrom,
        nextFilters.createdDateTo,
      );
      const normalizedFilters = {
        ...nextFilters,
        createdDateFrom: createdDateRange.createdAtFrom,
        createdDateTo: createdDateRange.createdAtTo,
      };

      setLoading(true);
      try {
        const response = await getReservationBookingApproveHistoriesApi({
          createdAtFrom: normalizedFilters.createdDateFrom,
          createdAtTo: normalizedFilters.createdDateTo,
          reservationNo: normalizedFilters.reservationNo?.trim() || undefined,
          approveStatus:
            normalizedFilters.approveStatus ||
            'PendingApprove,PaymentRejected,Approved,Cancelled',
          paymentMethod: normalizedFilters.paymentMethod || undefined,
          agentId: normalizedFilters.agentId || undefined,
          page: nextPagination.pageIndex + 1,
          pageSize: nextPagination.pageSize,
        });
        const payload = getResponsePayload(response);
        const items = getResponseItems(payload);

        setData(items);
        setTotalCount(getResponseTotal(payload, items));
        setPagination(nextPagination);
        setFilters(normalizedFilters);
      } catch (error) {
        setData([]);
        setTotalCount(0);
        showReservationErrorToast(error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination],
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTick(tick => tick + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [{ data: paymentData }, agentResponse] = await Promise.all([
          getLOVPaymentMethodReservation(),
          getDataAgentApi(),
        ]);

        setPaymentMethodOptions(Array.isArray(paymentData) ? paymentData : []);
        setAgentOptions(
          Array.isArray(agentResponse?.data?.data)
            ? agentResponse.data.data.map((agent: any) => ({
                label: agent.name,
                value: agent.id?.toString(),
              }))
            : [],
        );
      } catch {
        setPaymentMethodOptions([]);
        setAgentOptions([]);
      }
    };

    fetchLookups();
  }, []);

  const approvalModalRows = useMemo<ApprovalConfirmModalRow[]>(
    () => [
      {
        label: t('b2b.restaurantHistory.reservationCode', 'Mã đặt bàn'),
        value: selectedItem?.reservationNo,
      },
      {
        label: t('b2b.restaurant.filter.bookingDate', 'Ngày đặt'),
        value: formatDateTime(selectedItem?.createdAt),
      },
      {
        label: t('b2b.txt.orderedBy', 'Người đặt'),
        value: selectedItem?.agent?.name,
      },
      {
        label: t('b2b.restaurantHistory.guestAmount', 'Số lượng khách'),
        value: selectedItem?.partySize,
      },
      {
        label: t('b2b.txt.paymentMethod', 'Phương thức thanh toán'),
        value: getPaymentMethodLabel(
          selectedItem?.paymentMethod,
          paymentMethodOptions,
        ),
      },
      {
        label: t('b2b.restaurantHistory.moneyType', 'Loại tiền'),
        value: t('b2b.restaurantHistory.tableDeposit', 'Đặt cọc bàn'),
      },
      {
        label: t('b2b.txt.totalAmount', 'Tổng tiền'),
        value: formatMoney(selectedItem?.price, selectedItem?.currency?.value),
        valueClassName: '!text-[var(--color-9)]',
      },
    ],
    [paymentMethodOptions, selectedItem, t],
  );

  const handleSearch = () => {
    const values = getValues();
    const createdDateRange = getCreatedDateSearchRange(
      values.createdDateFrom,
      values.createdDateTo,
    );
    const nextFilters = {
      ...values,
      createdDateFrom: createdDateRange.createdAtFrom,
      createdDateTo: createdDateRange.createdAtTo,
    };

    setValue('createdDateFrom', nextFilters.createdDateFrom, {
      shouldDirty: true,
    });
    setValue('createdDateTo', nextFilters.createdDateTo, {
      shouldDirty: true,
    });
    fetchData({ pageIndex: 0, pageSize: pagination.pageSize }, nextFilters);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    fetchData({ pageIndex: page - 1, pageSize }, filters);
  };

  const handleAction = async (
    approveStatus: ApproveReservationPaymentPayload['approveStatus'],
  ) => {
    const orderId = selectedItem?.orderId?.toString();

    if (!orderId) {
      showReservationErrorToast({
        message: t(
          'b2b.restaurantHistory.missingReservationId',
          'Thiếu mã đặt bàn để xử lý.',
        ),
      });
      return;
    }

    if (isExpired(getExpireAt(selectedItem))) {
      showReservationErrorToast({
        message:
          translations?.MSG059 ??
          'Đơn đặt bàn đã hết thời gian giữ.',
      });
      return;
    }

    setActionLoading(approveStatus);
    try {
      await approveReservationPaymentApi(
        { orderId, approveStatus },
        { skipErrorNotify: true },
      );
      await fetchData(pagination, filters);
    } catch (error) {
      showReservationErrorToast(error);
    } finally {
      setSelectedItem(null);
      setActionLoading(undefined);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'createdAt',
        title: t('b2b.restaurant.filter.bookingDate', 'Ngày đặt bàn'),
        width: '190px',
        render: (item: ReservationHistoryItem) =>
          formatDateTime(item.createdAt),
      },
      {
        key: 'reservationNo',
        title: t('b2b.restaurantHistory.reservationCode', 'Mã đặt bàn'),
        width: '190px',
        render: (item: ReservationHistoryItem) => (
          <button
            type="button"
            className="border-0 bg-transparent p-0 font-semibold text-[#f5b51b] underline"
            onClick={() =>
              history.push(`/reservation-history/${item.reservationNo}`)
            }
          >
            {item.reservationNo}
          </button>
        ),
      },
      {
        key: 'agent',
        title: t('b2b.txt.orderedBy', 'Người đặt bàn'),
        width: '190px',
        render: (item: ReservationHistoryItem) => item?.agent?.name,
      },
      {
        key: 'paymentMethod',
        title: t('b2b.txt.paymentMethod', 'PTTT'),
        width: '170px',
        render: (item: ReservationHistoryItem) =>
          getPaymentMethodLabel(item.paymentMethod, paymentMethodOptions),
      },
      {
        key: 'moneyType',
        title: t('b2b.restaurantHistory.moneyType', 'Loại tiền'),
        width: '150px',
        render: (item: ReservationHistoryItem) => item.currency?.value,
      },
      {
        key: 'price',
        title: t('b2b.restaurantHistory.totalDeposit', 'Tổng tiền cọc'),
        width: '170px',
        render: (item: ReservationHistoryItem) =>
          formatMoney(item.price, item.currency?.value),
      },
      {
        key: 'expireAt',
        title: t('b2b.orderManagement.holdingTime', 'Thời gian giữ bàn'),
        width: '170px',
        render: (item: ReservationHistoryItem) => {
          const expireAt = getExpireAt(item);
          if (!shouldShowHoldTimeCountdown(item.approveStatus)) return '--';

          const expiredLabel = t('b2b.restaurantHistory.expired', 'Hết hạn');
          const countdown = getCountdown(expireAt, expiredLabel);
          return (
            <span
              className={cn(
                'font-semibold',
                isExpired(expireAt) && 'text-[#e92127]',
              )}
            >
              {countdown}
            </span>
          );
        },
      },
      {
        key: 'partySize',
        title: t('b2b.restaurantHistory.guestAmount', 'Số lượng khách'),
        width: '150px',
        render: (item: ReservationHistoryItem) => item.partySize ?? '--',
      },
      {
        key: 'approveStatus',
        title: t(
          'b2b.restaurantHistory.approveStatus',
          'Trạng thái duyệt',
        ),
        width: '170px',
        render: (item: ReservationHistoryItem) => (
          <span
            className={cn(
              'inline-flex min-h-[22px] items-center justify-center rounded-full px-3 py-1 text-xs font-semibold leading-none',
              getApproveStatusBadgeClass(item.approveStatus),
            )}
          >
            {getApproveStatusLabel(language, item.approveStatus)}
          </span>
        ),
      },
      {
        key: 'approveAgent',
        title: t('b2b.restaurantHistory.approveAgent', 'Người duyệt'),
        width: '170px',
        render: (item: ReservationHistoryItem) =>
          item?.approveAgent?.name ?? '--',
      },
      {
        key: 'approveTime',
        title: t(
          'b2b.restaurantHistory.approveHistory',
          'Lịch sử phê duyệt',
        ),
        width: '190px',
        render: (item: ReservationHistoryItem) =>
          formatDateTime(item.approveTime),
      },
      {
        key: 'action',
        title: t('b2b.txt.action', 'Thao tác'),
        width: '130px',
        render: (item: ReservationHistoryItem) => {
          if (item.approveStatus !== 'PendingApprove') return null;

          return (
            <Tooltip
              title={t('b2b.txt.approveNow', 'Phê duyệt ngay')}
            >
              <button
                type="button"
                className={ACTION_ICON_BUTTON_CLASS}
                onClick={() => {
                  if (isExpired(getExpireAt(item))) {
                    showReservationErrorToast({
                      message:
                        translations?.MSG059 ??
                        'Đơn đặt bàn đã hết thời gian giữ.',
                    });
                    return;
                  }
                  setSelectedItem(item);
                }}
              >
                <SafetyCertificateFilled className="text-xl" />
              </button>
            </Tooltip>
          );
        },
      },
    ],
    [history, language, paymentMethodOptions, t, translations],
  );

  return (
    <div className="p-6">
      <ReservationApprovalFilterStyles />
      <div className="mb-5 grid grid-cols-3 gap-3 max-lg:grid-cols-2 max-sm:grid-cols-1 [&>button]:w-full">
        <DatePickerWithRange
          value={createdDateRange}
          label={t('b2b.restaurant.filter.bookingDate', 'Ngày đặt')}
          startDateLabel={t('b2b.restaurantHistory.fromDate', 'Từ ngày')}
          endDateLabel={t('b2b.restaurantHistory.toDate', 'Đến ngày')}
          defaultDateTimeRange={defaultDateRange}
          maxDateRange={30}
          allowToDateWithoutFrom
          className={DATE_RANGE_FILTER_CLASS}
          classNameIcon="!h-4 !w-4 !text-[#6b7280]"
          classNameText="!text-left !text-sm !font-normal"
          classNamePlaceholder="!text-[#9b9b9b] !italic"
          onChange={range => {
            setValue('createdDateFrom', getDateParam(range?.from), {
              shouldDirty: true,
            });
            setValue('createdDateTo', getDateParam(range?.to), {
              shouldDirty: true,
            });
          }}
        />
        <SearchInput
          placeholder={t(
            'b2b.restaurantHistory.reservationCode',
             'Mã đặt bàn'
          )}
          registration={register('reservationNo')}
        />
        <Controller
          control={control}
          name="approveStatus"
          render={({ field }) => (
            <MultiSelectPopover
              value={field.value ?? null}
              options={approveStatusOptions}
              placeholder={t(
                'b2b.restaurantHistory.approveStatus',
                'Trạng thái duyệt',
              )}
              isSingleSelect
              allowDeselect
              isCloseAfterSelect
              className="w-full"
              storeFilterName="approve-order?tab=reservation_trangthaiduyet"
              classNameBtn={CONTROL_BUTTON_CLASS}
              onBlur={field.onBlur}
              onChange={value => field.onChange(value ? `${value}` : undefined)}
            />
          )}
        />
        <Controller
          control={control}
          name="paymentMethod"
          render={({ field }) => (
            <MultiSelectPopover
              value={field.value ?? null}
              options={paymentMethodOptions.map(item => ({
                label: item.name ?? item.code,
                value: `${item.id ?? item.code ?? item.mapVGS}`,
              }))}
              placeholder={t(
                'b2b.txt.paymentMethod',
                'Chọn PTTT',
              )}
              isSingleSelect
              allowDeselect
              isCloseAfterSelect
              className="w-full"
              classNameBtn={CONTROL_BUTTON_CLASS}
              onBlur={field.onBlur}
              storeFilterName="approve-order?tab=reservation_pttt"
              onChange={value => field.onChange(value ? `${value}` : undefined)}
            />
          )}
        />
        <Controller
          control={control}
          name="agentId"
          render={({ field }) => (
            <MultiSelectPopover
              value={field.value ?? null}
              options={agentOptions}
              placeholder={t(
                'b2b.txt.orderedBy',
                'Người đặt',
              )}
              isSingleSelect
              allowDeselect
              isCloseAfterSelect
              className="w-full"
              classNameBtn={CONTROL_BUTTON_CLASS}
              onBlur={field.onBlur}
              onChange={value => field.onChange(value ? `${value}` : undefined)}
            />
          )}
        />
        <StyledButton width="130px" height="40px" onClick={handleSearch}>
          {t('b2c.txt.search', 'Tìm kiếm')}
        </StyledButton>
      </div>

      <DataTableServer
        columns={columns}
        data={data}
        totalCount={totalCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        loading={loading}
        rowKey={(item, index) => `${item.reservationNo}-${index}`}
        onPaginationChange={handlePaginationChange}
        emptyText={t(
          'b2b.txt.no-result-found',
          'Không tìm thấy dữ liệu',
        )}
      />

      <ApprovalConfirmModal
        open={Boolean(selectedItem)}
        title={t(
          'b2b.restaurantHistory.approvePayment',
          'Phê duyệt thanh toán',
        )}
        rows={approvalModalRows}
        rejectLabel={t('b2b.txt.reject', 'Từ chối')}
        confirmLabel={t('b2b.txt.agree', 'Đồng ý')}
        rejectLoading={actionLoading === 'PaymentRejected'}
        confirmLoading={actionLoading === 'Approved'}
        disabled={Boolean(actionLoading)}
        onClose={() => setSelectedItem(null)}
        onReject={() => handleAction('PaymentRejected')}
        onConfirm={() => handleAction('Approved')}
      />
    </div>
  );
};

const SearchInput = ({
  placeholder,
  registration,
  type = 'text',
}: {
  placeholder?: string;
  registration: UseFormRegisterReturn;
  type?: string;
}) => (
  <input
    type={type}
    placeholder={placeholder}
    className="h-10 rounded-lg border border-solid border-[#d9d9d9] px-3 text-sm italic outline-none placeholder:text-[#9b9b9b] focus:border-[#f5b51b]"
    {...registration}
  />
);
